import React, { useContext, useEffect, useState } from 'react';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Box, Button, Grid, Radio, Typography } from '@mui/material';
import CustomDeleteDialog from '../../uiParts/CustomDeleteDialog';
import CustomLoading from '../../uiParts/CustomLoading';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import SideBar from '../../uiParts/SideBar';
import { sideBarWidth } from '../../../constants/Location';
import {
  black,
  gray100,
  gray70,
  gray90,
  primary10,
  primaryButtonBackgroundColor,
  primaryButtonOutlinedColor,
  primaryColor,
  tertiaryButtonOutlinedColor
} from '../../../constants/Colors';
import { CreateOutlined, DeleteOutline } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { AxiosError } from 'axios';
import { AuthContext } from '../../../contexts/auth';
import { common } from '../../../modules/error';
import { response } from '../../../api/db';
import {
  changeCo2SettingPattern,
  deleteCo2Setting,
  getCo2SettingList
} from '../../../api/db/co2Setting';
import { Co2SettingListType } from '../../../validation/co2Setting/co2SettingType';
import CustomHead from '../CustomHead';
import { sentryLog } from '../../../config/sentry';

const styles = {
  wrapper: {
    m: '32px',
    '@media screen and (max-width:600px)': {
      mb: '144px'
    }
  },
  headerContainer: {
    mb: '25px'
  },
  title: {
    '@media screen and (max-width:600px)': {
      fontSize: '24px'
    },
    fontSize: '32px',
    color: gray100,
    fontWeight: 500
  },
  saveButton: {
    ...primaryButtonBackgroundColor,
    p: '10px 16px',
    borderRadius: '4px',
    fontSize: '16px',
    height: '44px',
    '@media screen and (max-width:960px)': {
      display: 'none'
    }
  },
  spButtonContainer: {
    width: '100vw',
    backgroundColor: 'rgba(255, 255, 255, 0.80)',
    p: '12px 16px',
    position: 'fixed',
    bottom: '0',
    left: '0',
    zIndex: 1,
    '@media screen and (min-width:961px)': {
      display: 'none'
    }
  },
  saveButtonSp: {
    ...primaryButtonBackgroundColor,
    p: '10px 16px',
    borderRadius: '4px',
    fontSize: '16px',
    width: '95%',
    height: '44px'
  },
  tableContainer: {
    border: '1px solid' + gray70,
    borderBottom: 'none',
    minWidth: '1136px',
    overflowX: 'scroll'
  },
  headCell: {
    fontSize: '16px',
    fontWeight: 500,
    color: gray90,
    p: '10px 32px',
    borderRight: '1px solid' + gray70,
    borderBottom: '1px solid' + gray70
  },
  cell: {
    fontSize: '16px',
    fontWeight: 500,
    color: gray100,
    borderRight: '1px solid' + gray70,
    borderBottom: '1px solid' + gray70,
    // height: '48px',
    display: 'flex',
    alignItems: 'center'
  },
  usingText: {
    fontSize: '14px',
    flexBasis: '80px'
  },
  copyButton: {
    ...primaryButtonOutlinedColor,
    boxSizing: 'border-box',
    width: '196px'
  },
  createButton: {
    ...primaryButtonOutlinedColor,
    boxSizing: 'border-box',
    p: '5px 16px',
    width: '90px'
  },
  deleteButton: {
    ...tertiaryButtonOutlinedColor,
    boxSizing: 'border-box',
    p: '5px 16px',
    width: '90px'
  },
  backButton: {
    color: primaryColor,
    mt: '24px'
  }
};

const Co2StandardList = (): React.ReactElement => {
  const navigate = useNavigate();
  const { checkLoginStatus, userId, userRank, userType } =
    useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [infoText, setInfoText] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [isCheckedId, setIsCheckedId] = React.useState<number>();
  const [isDefaultId, setIsDefaultId] = React.useState<number>();
  const [isUsingId, setIsUsingId] = React.useState<number>();
  const [isButtonDisabled, setIsButtonDisabled] = React.useState<boolean>(true);
  const [co2StandardList, setCo2StandardList] = useState<Co2SettingListType[]>(
    []
  );
  const [deleteId, setDeleteId] = useState<number>();

  useEffect(() => {
    void (async () => {
      setLoading(true);
      if (!checkLoginStatus) return navigate('/auth/login');
      const result = await checkLoginStatus();
      if (!result.check) {
        navigate('/auth/login');
        return;
      }
      try {
        // 保存したCO2基準値一覧の取得
        const fetchedData = await getCo2SettingList();

        const usingData = fetchedData.filter(
          (item: Co2SettingListType) => item.use_flg === 1
        );
        setIsUsingId(usingData[0].id);
        setIsCheckedId(usingData[0].id);

        const defaultData = fetchedData.filter(
          (item: Co2SettingListType) => item.default_flg === 1
        );
        setIsDefaultId(defaultData[0].id);

        const displayData = fetchedData.map((data: Co2SettingListType) => {
          // 日付の表示形式を変更
          const createdDate = new Date(data.created_at);
          const formattedCreatedDate = createdDate
            .toISOString()
            .slice(0, 10)
            .replace(/-/g, '/');
          const updatedDate = new Date(data.updated_at);
          const formattedUpdatedDate = updatedDate
            .toISOString()
            .slice(0, 10)
            .replace(/-/g, '/');
          return {
            id: data.id,
            name: data.name,
            use_flg: data.use_flg,
            default_flg: data.default_flg,
            created_at: formattedCreatedDate,
            updated_at: formattedUpdatedDate
          };
        });
        setCo2StandardList(displayData);
        setLoading(false);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        sentryLog(error);
        const axiosError = error as AxiosError;
        if (axiosError.isAxiosError && axiosError.response) {
          const loginErrorResponse = axiosError.response as response;
          setErrorText(loginErrorResponse.data.message);
          setSnackbarOpen(true);
        } else {
          setErrorText(common);
          setSnackbarOpen(true);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    setIsButtonDisabled(isCheckedId === isUsingId);
  }, [isCheckedId, isUsingId]);

  const onClickSaveButton = async (): Promise<void> => {
    setLoading(true);
    setErrorText('');
    setInfoText('');
    try {
      if (!checkLoginStatus) return navigate('/auth/login');
      const result = await checkLoginStatus();
      if (!result.check) {
        navigate('/auth/login');
        return;
      }
      if (!isCheckedId) {
        setErrorText(common);
        setSnackbarOpen(true);
        return;
      }
      // 利用するCO2基準値を変更
      await changeCo2SettingPattern(isCheckedId);
      setIsUsingId(isCheckedId);
      setInfoText('利用するCO2基準値を変更しました');
      window.dataLayer.push({
        event: 'standard_change',
        screen: window.location.pathname,
        category: 'button',
        action: 'standard_list_change',
        user_id: userId,
        user_type: userType,
        user_rank: userRank
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const errorResponse = axiosError.response as response;
        setErrorText(errorResponse.data.message);
      } else {
        setErrorText(common);
      }
    } finally {
      setLoading(false);
      setSnackbarOpen(true);
    }
  };

  const onDelete = async (): Promise<void> => {
    setLoading(true);
    setErrorText('');
    setInfoText('');
    try {
      if (!deleteId) {
        setErrorText(common);
        setSnackbarOpen(true);
        return;
      }
      // CO2基準値の削除
      await deleteCo2Setting(deleteId);

      // 削除したCO2基準値を一覧から削除
      const updatedCo2StandardList = co2StandardList.filter(
        (item) => item.id !== deleteId
      );
      setCo2StandardList(updatedCo2StandardList);
      setDeleteId(0);
      setInfoText('CO2基準値を削除しました');
      window.dataLayer.push({
        event: 'standard_del',
        screen: window.location.pathname,
        category: 'button',
        action: 'standard_list_delete',
        user_id: userId,
        user_type: userType,
        user_rank: userRank
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const errorResponse = axiosError.response as response;
        setErrorText(errorResponse.data.message);
      } else {
        setErrorText(common);
      }
    } finally {
      setDialogOpen(false);
      setSnackbarOpen(true);
      setLoading(false);
    }
  };

  return (
    <>
      <CustomHead title="保存したCO2基準値 | CMCOS[コムコス]" />
      <CustomLoading loading={loading} />
      <Grid width="100vw">
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <SideBar />
        </Box>
        <Grid
          sx={styles.spButtonContainer}
          container
          justifyContent="center"
          alignItems="center"
        >
          <Button
            sx={styles.saveButtonSp}
            disabled={isButtonDisabled}
            onClick={onClickSaveButton}
          >
            基準値を変更する
          </Button>
        </Grid>
        <Grid
          sx={{
            '@media screen and (min-width:961px)': {
              ml: sideBarWidth
            }
          }}
        >
          <Box sx={styles.wrapper}>
            <Grid
              container
              justifyContent="space-between"
              sx={styles.headerContainer}
            >
              <Grid item>
                <Typography sx={styles.title}>保存したCO2基準値</Typography>
              </Grid>
              <Grid item>
                <Button
                  sx={styles.saveButton}
                  disabled={isButtonDisabled}
                  onClick={onClickSaveButton}
                >
                  基準値を変更する
                </Button>
              </Grid>
            </Grid>
            <Grid container sx={styles.tableContainer}>
              <Grid container>
                <Grid item xs={4} sx={[styles.headCell, { pl: '148px' }]}>
                  <Typography>名前</Typography>
                </Grid>
                <Grid item xs={2} sx={styles.headCell}>
                  <Typography>登録日</Typography>
                </Grid>
                <Grid item xs={2} sx={styles.headCell}>
                  <Typography>更新日</Typography>
                </Grid>
                <Grid
                  item
                  xs={4}
                  sx={[styles.headCell, { borderRight: 'none' }]}
                />
              </Grid>
              {co2StandardList.map((data) => (
                <TableRow
                  key={data.id}
                  data={data}
                  isChecked={isCheckedId === data.id}
                  isDefault={isDefaultId === data.id}
                  isUsing={isUsingId === data.id}
                  setDeleteId={setDeleteId}
                  setDialogOpen={setDialogOpen}
                  onClick={() => {
                    setIsCheckedId(data.id);
                  }}
                />
              ))}
            </Grid>
            <Button
              sx={styles.backButton}
              onClick={() => navigate(`/standard`)}
            >
              <ChevronLeftIcon sx={{ mr: '8px' }} />
              戻る
            </Button>
          </Box>
        </Grid>
      </Grid>

      <CustomDeleteDialog
        setDialogOpen={setDialogOpen}
        onDelete={() => onDelete()}
        open={dialogOpen}
        title="CO2基準値を削除"
      />
      <CustomSnackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        infoText={infoText}
        onClose={() => setSnackbarOpen(false)}
        text={errorText}
      />
    </>
  );
};

export default Co2StandardList;

const TableRow = (props: {
  data: Co2SettingListType;
  isUsing: boolean;
  isChecked: boolean;
  isDefault: boolean;
  setDeleteId: (value: number) => void;
  setDialogOpen: (value: boolean) => void;
  onClick: () => void;
}): React.ReactElement => {
  const {
    data,
    isUsing,
    isChecked,
    isDefault,
    setDeleteId,
    setDialogOpen,
    onClick
  } = props;
  const navigate = useNavigate();

  return (
    <Grid container sx={{ backgroundColor: isChecked ? primary10 : 'none' }}>
      <Grid item xs={4} sx={[styles.cell, { pl: '24px' }]}>
        <Grid container alignItems="center" flexWrap="nowrap">
          <Box sx={{ width: '42px' }}>
            <Radio
              sx={{ width: '24px' }}
              checked={isChecked}
              onClick={onClick}
            />
          </Box>
          <Typography sx={styles.usingText}>
            {isUsing ? '利用中' : ''}
          </Typography>
          <Typography
            sx={{ color: isDefault ? gray90 : black, width: '231px' }}
          >
            {data.name}
          </Typography>
        </Grid>
      </Grid>
      <Grid item xs={2} sx={[styles.cell, { pl: '32px' }]}>
        <Typography sx={{ color: isDefault ? gray90 : black }}>
          {data.created_at}
        </Typography>
      </Grid>
      <Grid item xs={2} sx={[styles.cell, { pl: '32px' }]}>
        <Typography sx={{ color: isDefault ? gray90 : black }}>
          {data.updated_at}
        </Typography>
      </Grid>
      <Grid item xs={4} sx={[styles.cell, { pr: '32px', borderRight: 'none' }]}>
        <Grid container justifyContent="flex-end">
          {isDefault ? (
            <Button
              startIcon={<CreateOutlined />}
              sx={styles.copyButton}
              variant="outlined"
              onClick={() => navigate(`/standard/${data.id}`)}
            >
              コピーを作成
            </Button>
          ) : (
            <>
              <Grid item sx={{ pr: '16px' }}>
                <Button
                  variant="outlined"
                  startIcon={<CreateOutlined />}
                  sx={styles.createButton}
                  onClick={() => navigate(`/standard/${data.id}`)}
                >
                  編集
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  startIcon={<DeleteOutline />}
                  sx={styles.deleteButton}
                  onClick={() => {
                    setDialogOpen(true);
                    setDeleteId(data.id);
                  }}
                >
                  削除
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};
